import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EN_COURS = "bkprod_connexion_en_cours";
const DUREE_MS = 15 * 60 * 1000; // validité d'un code

/** Mémorise la connexion en cours : sur iPhone, aller chercher le code dans Gmail décharge la page. */
const lireEnCours = () => {
  try {
    const p = JSON.parse(localStorage.getItem(EN_COURS) || "null");
    if (!p || Date.now() - p.t > DUREE_MS) {
      localStorage.removeItem(EN_COURS);
      return null;
    }
    return p;
  } catch {
    return null;
  }
};

const ecrireEnCours = (p) => localStorage.setItem(EN_COURS, JSON.stringify({ ...p, t: Date.now() }));
const effacerEnCours = () => localStorage.removeItem(EN_COURS);

export default function Login() {
  const reprise = lireEnCours();
  const [step, setStep] = useState(reprise?.step === "otp" ? "otp" : "identifier");
  const [identifier, setIdentifier] = useState(reprise?.identifier || "");
  const [code, setCode] = useState("");
  const [debugCode, setDebugCode] = useState(reprise?.debugCode || "");
  const [masque, setMasque] = useState(reprise?.masque || "");
  const [canal, setCanal] = useState(reprise?.canal || "email");
  const [googleEnAttente, setGoogleEnAttente] = useState(reprise?.step === "google");
  const [nom, setNom] = useState("");
  const [societe, setSociete] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // iOS décharge la page quand on bascule vers Gmail : on restaure l'étape au retour.
  useEffect(() => {
    const restaurer = () => {
      const p = lireEnCours();
      if (!p) return;
      if (p.step === "otp") {
        setIdentifier(p.identifier || "");
        setMasque(p.masque || "");
        setCanal(p.canal || "email");
        setDebugCode(p.debugCode || "");
        setStep((actuel) => (actuel === "identifier" ? "otp" : actuel));
      } else if (p.step === "google") {
        setGoogleEnAttente(true);
      }
    };
    const surRetour = () => document.visibilityState === "visible" && restaurer();
    document.addEventListener("visibilitychange", surRetour);
    window.addEventListener("pageshow", restaurer);
    return () => {
      document.removeEventListener("visibilitychange", surRetour);
      window.removeEventListener("pageshow", restaurer);
    };
  }, []);

  const lancerGoogle = () => {
    ecrireEnCours({ step: "google" });
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const askOtp = async () => {
    if (!identifier.trim()) return toast.error("Saisissez votre email ou téléphone");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { identifier });
      if (!data.found) {
        setStep("inconnu");
      } else {
        setCanal(data.canal);
        setDebugCode(data.debug_code || "");
        setMasque(data.masque || identifier);
        setStep("otp");
        ecrireEnCours({
          step: "otp",
          identifier,
          masque: data.masque || identifier,
          canal: data.canal,
          debugCode: data.debug_code || "",
        });
        toast.success(data.message);
      }
    } catch (e) {
      toast.error(errMsg(e, "Erreur réseau"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { identifier, code });
      effacerEnCours();
      await login(data.token);
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e, "Code invalide"));
    } finally {
      setBusy(false);
    }
  };

  // Code collé auto : au retour sur l'appli (depuis l'email), on lit le presse-papier.
  useEffect(() => {
    if (step !== "otp") return;
    const lirePressePapier = async () => {
      if (document.visibilityState !== "visible") return;
      if (!navigator.clipboard?.readText) return;
      try {
        const texte = await navigator.clipboard.readText();
        const trouve = (texte || "").match(/\b\d{6}\b/) || (texte || "").replace(/\D/g, "").match(/^\d{6}$/);
        if (!trouve) return;
        const found = trouve[0];
        setCode((actuel) => {
          if (actuel.length === 6) return actuel;
          toast.success("Code collé automatiquement");
          return found;
        });
      } catch {
        /* autorisation refusée : saisie manuelle */
      }
    };
    lirePressePapier();
    document.addEventListener("visibilitychange", lirePressePapier);
    window.addEventListener("focus", lirePressePapier);
    return () => {
      document.removeEventListener("visibilitychange", lirePressePapier);
      window.removeEventListener("focus", lirePressePapier);
    };
  }, [step]);

  const sendRattachement = async () => {    setBusy(true);
    try {
      const { data } = await api.post("/auth/rattachement", { identifier, nom, societe, message });
      toast.success(data.message);
      setStep("identifier");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bk-grid-bg flex flex-col">
      <div className="flex-1 flex items-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md mx-auto">
          <div className="bk-label mb-3">Espace client BKPRO</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            Déclarez votre
            <br />
            panne en 2 min.
          </h1>
          <p className="text-base leading-relaxed text-slate-600 mt-4 mb-10">
            Diagnostic guidé, photos, message vocal. Votre technicien reçoit tout, immédiatement.
          </p>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_0_rgba(15,23,42,0.04),0_18px_40px_-24px_rgba(15,23,42,0.25)]">
            {step === "identifier" && (
              <div className="space-y-4">
                {googleEnAttente && (
                  <div className="rounded-xl border border-[#005CFF]/30 bg-blue-50/70 px-4 py-3 text-left space-y-2" data-testid="login-google-reprise">
                    <p className="text-sm text-slate-700">
                      Votre connexion Google a été interrompue (validation dans Gmail, changement d'application…).
                      Reprenez-la ici, vous n'avez rien perdu.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid="login-google-reprise-btn" onClick={lancerGoogle} size="sm" className="bg-[#005CFF] hover:bg-[#0047CC]">
                        <RotateCcw size={14} className="mr-1.5" /> Reprendre avec Google
                      </Button>
                      <Button
                        data-testid="login-google-abandon-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          effacerEnCours();
                          setGoogleEnAttente(false);
                        }}
                      >
                        Utiliser un code par email
                      </Button>
                    </div>
                  </div>
                )}
                <label className="bk-label block">Email ou téléphone</label>
                <Input
                  data-testid="login-identifier-input"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && askOtp()}
                  placeholder="client@uexpress.fr"
                  className="h-14 text-base"
                />
                <Button data-testid="login-request-otp-btn" onClick={askOtp} disabled={busy} className="w-full h-14 text-base rounded-2xl bg-[#005CFF] hover:bg-[#0047CC] font-semibold bk-glow">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <>Recevoir mon code <ArrowRight size={18} className="ml-2" /></>}
                </Button>

                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400 py-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span>ou</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <Button
                  data-testid="login-google-btn"
                  variant="outline"
                  onClick={lancerGoogle}
                  className="w-full h-14 text-base rounded-2xl border-slate-300 font-semibold"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" className="mr-3" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.6l-6.5-5.5C29.6 34.5 26.9 36 24 36c-5.3 0-9.7-3.1-11.3-8l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.4l6.5 5.5C41.4 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z" />
                  </svg>
                  Continuer avec Google
                </Button>
                <p className="text-xs text-slate-500 flex items-start gap-2 pt-2">
                  <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                  Sans mot de passe. Votre compte est vérifié auprès de notre CRM Axonaut.
                </p>
              </div>
            )}

            {step === "otp" && (
              <div className="space-y-4">
                <label className="bk-label block">Code reçu par {canal === "email" ? "email" : "écran"}</label>
                <Input
                  data-testid="login-otp-input"
                  value={code}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onPaste={(e) => {
                    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                    if (t) {
                      e.preventDefault();
                      setCode(t);
                    }
                  }}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  placeholder="••••••"
                  className="h-16 text-center text-2xl tracking-[0.5em] font-mono-bk"
                />
                {debugCode && (
                  <div data-testid="login-debug-code" className="text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
                    Mode démo — code : <strong className="font-mono-bk">{debugCode}</strong>
                  </div>
                )}
                {!debugCode && (
                  <p className="text-xs text-slate-500" data-testid="login-otp-hint">
                    Code envoyé à {masque}. Pensez à vérifier vos spams.
                  </p>
                )}
                <Button data-testid="login-verify-btn" onClick={verify} disabled={busy || code.length < 6} className="w-full h-14 text-base rounded-xl bg-[#005CFF] hover:bg-[#0047CC]">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : "Se connecter"}
                </Button>
                <button data-testid="login-back-btn" onClick={() => { effacerEnCours(); setStep("identifier"); }} className="text-sm text-slate-500 hover:text-slate-900">
                  Modifier mon identifiant
                </button>
              </div>
            )}

            {step === "inconnu" && (
              <div className="space-y-4" data-testid="login-unknown-panel">
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
                  Compte non reconnu. Demandez votre rattachement, BKPRO vous recontacte sous 48h.
                </div>
                <Input data-testid="rattach-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Votre nom" className="h-12" />
                <Input data-testid="rattach-societe" value={societe} onChange={(e) => setSociete(e.target.value)} placeholder="Société (optionnel)" className="h-12" />
                <Textarea data-testid="rattach-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Votre installation, adresse du chantier…" rows={3} />
                <Button data-testid="rattach-submit" onClick={sendRattachement} disabled={busy || !nom} className="w-full h-12 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]">
                  Envoyer ma demande
                </Button>
                <button onClick={() => setStep("identifier")} className="text-sm text-slate-500 hover:text-slate-900">Retour</button>
              </div>
            )}
          </div>

          <p className="text-sm text-slate-600 mt-3 max-w-sm" data-testid="login-note-equipe">
            Nous, on fait des pauses — pas BKPROD. L'équipe est humaine et souvent en intervention : l'application, elle,
            reste ouverte pour déclarer, suivre et vous dépanner à toute heure.
          </p>

          <a href="/admin/login" data-testid="admin-login-link" className="inline-block mt-8 text-xs font-semibold tracking-wider uppercase text-slate-400 hover:text-[#005CFF] transition-colors">
            Accès équipe BKPRO →
          </a>
          <a href="/realisations" data-testid="vitrine-publique-link" className="block mt-3 text-xs font-semibold tracking-wider uppercase text-slate-400 hover:text-[#005CFF] transition-colors">
            Découvrir nos réalisations →
          </a>
        </motion.div>
      </div>
    </div>
  );
}
